import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";

import { ui } from "../lib/ui";

/** What a search knows about a store. Fleet rows and `Branch` records both
 *  reduce to this, so one box serves both consoles. */
export interface SearchableBranch {
  id: string;
  name: string;
  governorate: string;
  area: string;
  /** Extra detail for the result line, e.g. "no camera" or "demo". */
  note?: string | null;
}

/** Case-insensitive match over the fields an operator would actually type:
 *  the store name, its area, its governorate and its id. Every whitespace-
 *  separated term has to hit, so "maadi hyper" narrows rather than widens. */
export function matchesBranch(branch: SearchableBranch, query: string): boolean {
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return true;
  const haystack =
    `${branch.name} ${branch.area} ${branch.governorate} ${branch.id}`.toLowerCase();
  return terms.every((term) => haystack.includes(term));
}

interface BranchSearchProps {
  /** The stores to search — already narrowed to what the page shows. */
  branches: SearchableBranch[];
  value: string;
  onChange: (query: string) => void;
  /** Picking a result; the caller decides what selection means. */
  onSelect?: (branch: SearchableBranch) => void;
  placeholder?: string;
  className?: string;
  /** Results in the dropdown; the rest need a narrower query. */
  limit?: number;
  "aria-label"?: string;
}

/** One store search, shared by the hub's Fleet table and the analytics
 *  filter bar: type any part of a name, area, governorate or id and pick a
 *  store from the live list. 462 branches, so it is never a list of chips. */
export function BranchSearch({
  branches,
  value,
  onChange,
  onSelect,
  placeholder = "Search name, area or id",
  className = "",
  limit = 8,
  "aria-label": ariaLabel = "Search branches",
}: BranchSearchProps) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const box = useRef<HTMLDivElement>(null);

  const matches = useMemo(
    () =>
      value.trim()
        ? branches.filter((b) => matchesBranch(b, value)).slice(0, limit)
        : [],
    [branches, value, limit],
  );
  const listed = open && matches.length > 0;

  // A click anywhere else closes the list; blur alone would fire before the
  // click on a result lands.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!box.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const choose = (branch: SearchableBranch) => {
    setOpen(false);
    onSelect?.(branch);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") {
      setOpen(false);
      return;
    }
    if (!listed) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : matches.length - 1;
      setActive((index) => (index + step) % matches.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      choose(matches[Math.min(active, matches.length - 1)]);
    }
  };

  return (
    <div ref={box} className={`relative ${className}`}>
      <input
        className={`${ui.input} w-full`}
        type="search"
        role="combobox"
        aria-expanded={listed}
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(event) => {
          onChange(event.target.value);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
      />
      {listed && (
        <ul
          role="listbox"
          className="absolute left-0 z-30 mt-1 max-h-72 w-full min-w-[260px] overflow-auto rounded-lg border border-border bg-surface py-1 shadow-lg"
        >
          {matches.map((branch, index) => (
            <li key={branch.id} role="option" aria-selected={index === active}>
              <button
                type="button"
                className={`flex w-full cursor-pointer flex-col items-start gap-0.5 px-3 py-1.5 text-left ${
                  index === active
                    ? "bg-[color-mix(in_srgb,var(--accent)_10%,transparent)]"
                    : ""
                }`}
                onMouseEnter={() => setActive(index)}
                onClick={() => choose(branch)}
              >
                <span className="text-sm text-text">{branch.name}</span>
                <span className="text-xs text-muted">
                  {branch.governorate} › {branch.area || "—"}
                  {branch.note ? ` · ${branch.note}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
